/**
 * Image URL optimizer.
 *
 * Rewrites image URLs to request properly-sized variants:
 * - Supabase Storage public URLs are routed through the
 *   `/storage/v1/render/image/public/...` transformation endpoint
 *   so the server returns a resized WebP (much smaller than originals).
 * - Unsplash URLs get/replace `w`, `h`, `q`, `auto=format`, `fit=crop` params.
 * - Other URLs are returned unchanged.
 *
 * This is purely a delivery optimization — no UI/UX change.
 */

type Opts = {
  width: number;
  height?: number;
  quality?: number;
};

const SUPABASE_PUBLIC_OBJECT = "/storage/v1/object/public/";
const SUPABASE_PUBLIC_RENDER = "/storage/v1/render/image/public/";

export function optimizedImageUrl(
  url: string | undefined | null,
  opts: Opts,
): string {
  if (!url) return "";
  const { width, height, quality = 75 } = opts;

  try {
    // Supabase Storage public URL → render/image transformation
    if (url.includes(SUPABASE_PUBLIC_OBJECT)) {
      const transformed = url.replace(
        SUPABASE_PUBLIC_OBJECT,
        SUPABASE_PUBLIC_RENDER,
      );
      const u = new URL(transformed);
      u.searchParams.set("width", String(width));
      if (height) u.searchParams.set("height", String(height));
      u.searchParams.set("resize", "cover");
      u.searchParams.set("quality", String(quality));
      return u.toString();
    }

    // Unsplash URL → query params
    if (url.includes("images.unsplash.com")) {
      const u = new URL(url);
      u.searchParams.set("w", String(width));
      if (height) u.searchParams.set("h", String(height));
      u.searchParams.set("fit", "crop");
      u.searchParams.set("auto", "format");
      u.searchParams.set("q", String(quality));
      return u.toString();
    }
  } catch {
    return url;
  }

  return url;
}

/**
 * Returns a `srcset` string for responsive delivery (1x and 2x).
 */
export function optimizedSrcSet(
  url: string | undefined | null,
  opts: Opts,
): string | undefined {
  if (!url) return undefined;
  const x1 = optimizedImageUrl(url, opts);
  const x2 = optimizedImageUrl(url, {
    ...opts,
    width: opts.width * 2,
    height: opts.height ? opts.height * 2 : undefined,
  });
  if (x1 === url && x2 === url) return undefined;
  return `${x1} 1x, ${x2} 2x`;
}
